import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { resolveApiBase } from "./apiBase";
import {
  USER_SESSION_EXPIRED_EVENT,
  clearUserTokens,
  getUserAccessToken,
  getUserRefreshToken,
} from "./userSession";

const AuthContext = createContext(null);
const SAFE_SESSION_IDLE_MS = 10 * 60 * 1000;
const LAST_ACTIVE_KEY = "paisacx.lastActive";
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];

function canAccessApp(rawUser, otpRequired) {
  if (!rawUser) return false;
  if (rawUser.email_verified && rawUser.mobile_verified) return true;
  // Finished signup stores email + mobile and verifies the phone later from Profile.
  if (rawUser.signup_complete && rawUser.email && rawUser.mobile_number) return true;
  // OTP disabled by admin — allow once mobile is saved; verify later from profile.
  if (!otpRequired && rawUser.mobile_number) return true;
  return false;
}

/**
 * AuthProvider
 *
 * Two-tier user state:
 *  - `user`         : account allowed into the app. Fully verified, OR
 *                     OTP verification disabled by admin (verify later from profile).
 *  - `pendingUser`  : a half-registered account that still must finish OTP
 *                     (only when OTP is required). Register page resumes from this.
 *
 * `null` on either means "still loading /me on first paint".
 */
export function AuthProvider({ children }) {
  // Raw /me result: null (loading) | false (anonymous) | object
  const [rawUser, setRawUser] = useState(null);
  const [otpVerificationRequired, setOtpVerificationRequired] = useState(true);
  const [otpSettingLoaded, setOtpSettingLoaded] = useState(false);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/api/auth/me");
      if (typeof data.otp_verification_required === "boolean") {
        setOtpVerificationRequired(data.otp_verification_required);
        setOtpSettingLoaded(true);
      }
      setRawUser(data.user || false);
      return data.user;
    } catch {
      setRawUser(false);
      return false;
    }
  }, []);

  useEffect(() => {
    // Always load the live OTP toggle so unverified OTP-off accounts can enter
    // even if /me is from an older backend build.
    api
      .get("/api/auth/signup-config")
      .then(({ data }) => {
        if (typeof data.otp_verification_required === "boolean") {
          setOtpVerificationRequired(data.otp_verification_required);
        }
      })
      .catch(() => {})
      .finally(() => setOtpSettingLoaded(true));
  }, []);

  useEffect(() => {
    // Skip consumer /me on admin routes (avoids noisy 401 in console)
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) {
      setRawUser(false);
      return;
    }
    // Cross-origin API (site :5011, API :5010) cannot see httpOnly cookies.
    // Probing /me with no bearer token only produces a 401.
    const base = resolveApiBase();
    const crossOrigin = Boolean(base) && base !== window.location.origin;
    if (crossOrigin && !getUserAccessToken() && !getUserRefreshToken()) {
      setRawUser(false);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  useEffect(() => {
    // An API call hit an unrecoverable 401. Re-check /me (it may race a fresh login)
    // and drop to anonymous if the session is really gone, so RequireAuth redirects.
    let verifying = false;
    const onExpired = async () => {
      if (verifying) return;
      verifying = true;
      try {
        await fetchMe();
      } finally {
        verifying = false;
      }
    };
    window.addEventListener(USER_SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(USER_SESSION_EXPIRED_EVENT, onExpired);
  }, [fetchMe]);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } catch {
      /* ignore */
    }
    clearUserTokens();
    setRawUser(false);
  }, []);

  const safeSession = Boolean(rawUser && rawUser.safe_session);
  useEffect(() => {
    if (!safeSession) return undefined;
    // Shared across tabs so activity in any open tab keeps the session alive.
    const touch = () => {
      try {
        localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
      } catch {
        /* storage unavailable */
      }
    };
    let lastWrite = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastWrite > 5000) {
        lastWrite = now;
        touch();
      }
    };
    touch();
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const timer = setInterval(async () => {
      let last = Date.now();
      try {
        last = Number(localStorage.getItem(LAST_ACTIVE_KEY)) || last;
      } catch {
        /* storage unavailable */
      }
      if (Date.now() - last >= SAFE_SESSION_IDLE_MS) {
        clearInterval(timer);
        await logout();
        window.location.assign("/login?reason=idle");
      }
    }, 15000);
    return () => {
      clearInterval(timer);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [safeSession, logout]);

  const setUser = useCallback((u, otpFlag) => {
    if (typeof otpFlag === "boolean") {
      setOtpVerificationRequired(otpFlag);
      setOtpSettingLoaded(true);
    }
    setRawUser(u || false);
  }, []);

  // Derive the public-facing user/pendingUser values.
  // Wait for OTP setting so we don't briefly treat OTP-off users as pending.
  let user;
  let pendingUser = null;
  if (rawUser === null || !otpSettingLoaded) {
    user = null;
  } else if (rawUser === false) {
    user = false;
  } else if (canAccessApp(rawUser, otpVerificationRequired)) {
    user = rawUser;
  } else {
    user = false;
    pendingUser = rawUser;
  }

  return (
    <AuthContext.Provider
      value={{ user, pendingUser, otpVerificationRequired, setUser, fetchMe, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
